import { inject, Injectable } from '@angular/core';
import { SettingsService } from '@services/settings.service';
import { UserService } from '@services/user.service';
import { ColumnCategory, ColumnDef, DataViewDomainSchema, DataViewPageSchema, FilterDef } from '@shared/dataview/core/dataview-types';
import { AccessContext, filterByAccess, userHasAccessToItem } from '@shared/dataview/config/access';
import { IUser } from '@shared/models/user.model';
import { map, Observable, of, switchMap } from 'rxjs';

export const DATAVIEW_PAGE_SETTINGS_KEY = 'dataview4';
export const DATAVIEW_DOMAIN_SETTINGS_KEY = 'dataview4-columns';

export interface DataViewDefinitions {
  columns: ColumnDef[];
  filters: FilterDef[];
  categories: ColumnCategory[];
}

@Injectable({ providedIn: 'root' })
export class DataViewDefinitionService {
  private readonly settingsService = inject(SettingsService);
  private readonly userService = inject(UserService);

  load(pageName: string, currentUser?: IUser): Observable<DataViewDefinitions> {
    return this.settingsService
      .find<DataViewPageSchema>(DATAVIEW_PAGE_SETTINGS_KEY, pageName, true)
      .pipe(switchMap(page => this.loadDomain(page).pipe(map(domain => this.resolve(page, domain, this.accessContext(currentUser))))));
  }

  private loadDomain(page: DataViewPageSchema | undefined): Observable<DataViewDomainSchema | undefined> {
    if (!page?.domain) return of(undefined);
    return this.settingsService.find<DataViewDomainSchema>(DATAVIEW_DOMAIN_SETTINGS_KEY, `${page.domain}_columns`, true);
  }

  private accessContext(currentUser?: IUser): AccessContext {
    return {
      user: currentUser ?? this.userService.getCurrentUser(),
      hasAllTenantsAccess: this.userService.hasAllTenantsAccess(),
    };
  }

  resolve(page: DataViewPageSchema | undefined, domain: DataViewDomainSchema | undefined, access: AccessContext): DataViewDefinitions {
    if (!page || !domain) return { columns: [], filters: [], categories: [] };

    const pageColumns = page.columns ?? [];
    const pageColumnById = new Map(pageColumns.map(column => [column.id, column]));

    const columns: ColumnDef[] = [];
    const filters: FilterDef[] = [];

    for (const [id, definition] of Object.entries(domain.columns ?? {})) {
      const onPage = pageColumnById.get(id);
      if (!onPage || !userHasAccessToItem(definition, access)) continue;

      const categoryId = definition.categoryId ?? '';

      if (!onPage.filterOnly) {
        columns.push({ ...definition, id, categoryId });
      }

      const filter = definition.filter;
      if (filter && !onPage.hideFilter && userHasAccessToItem(filter, access)) {
        filters.push({
          ...definition,
          ...filter,
          id,
          categoryId,
          display: filter.display ?? definition.display,
          field: filter.field ?? definition.field,
          required: onPage.requiredFilter ?? false,
        });
      }
    }

    const categories = filterByAccess(
      Object.entries(domain.columnCategories ?? {}).map(([id, category]) => ({ ...category, id })),
      access,
    );

    return { columns, filters, categories };
  }
}
